import ResizableImageEditor from '@vromo/dashboard/src/campaigns/ResizableImageEditor';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useStore } from '@vromo/mobx-react';
import { Widget } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import Input from '@vromo/ui/components/Input';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import Select from '@vromo/ui/components/Select';
import { useToasts } from '@vromo/ui/components/Toasts';
import { action, observable, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Campaign } from 'models/Campaign';
import React, { ChangeEvent, useRef, useState } from 'react';
import { deserialize, update } from 'serializr';
import { qaLabelCampaigns } from './../../../../../../../common-qa-labels/campaigns';
import styles from './widgetEditor.style';
import { promotionInputs, ratingInputs, youtubeID } from './widgetUtils';
import WidgetTriggerSelector from './components/WidgetTriggerSelector';
import InputSection from './components/InputSection';
import { useTranslation } from 'react-i18next';

interface Props {
    campaign: Campaign;
    editWidget?: Widget | null;
    editing: boolean;
    setEditing: React.Dispatch<React.SetStateAction<boolean>>;
    close: () => void;
}

const WidgetEditor = observer<Props>(({ campaign, close, editWidget, editing, setEditing }) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [widget, setWidget] = useState(() =>
        editWidget ? deserialize(Widget, editWidget) : new Widget()
    );

    const rootStore = useStore<RootStore>('rootStore');

    const file = useRef(null);

    const showToast = useToasts();

    const { graph } = rootStore.authStore;

    const addWidget = async () => {
        setLoading(true);

        if (widget.type === 'video') {
            const id = youtubeID(widget.content);

            if (!id) {
                setLoading(false);
                alert(t('widgets.invalidUrl'));
                return;
            }

            runInAction(() => (widget.content = id));
        }

        if (widget.type === 'promotion' || widget.type === 'image') {
            if (file?.current?.type) {
                try {
                    const response = await graph.root
                        .graph()
                        .user()
                        .uploads()
                        .post([{ type: file.current.type, link: 'campaign' }]);

                    await fetch(response?.items[0]?.upload, {
                        method: 'PUT',
                        body: file.current
                    });

                    runInAction(() => {
                        widget.content = response.items[0].url;
                    });
                } catch (e) {
                    console.error(e);
                    setLoading(false);
                    showToast(t('widgets.imageError'), 'error');
                }
            } else {
                setLoading(false);
                showToast(t('widgets.imageWarning'), 'warning');
            }
        }

        if (widget.type === 'address_correction') {
            widget.settings.popup === true;
        }

        runInAction(() => {
            if (!campaign?.widgets) {
                campaign.widgets = [];
            }

            if (editing) {
                const widgetIdx = campaign?.widgets?.findIndex((w) => w.id === widget.id);

                if (widgetIdx !== undefined && widgetIdx >= 0) {
                    campaign.widgets?.splice(widgetIdx, 1, toJS(widget));
                }
            } else {
                campaign.widgets.push(toJS(widget));
            }
        });

        setLoading(false);
        setWidget(observable(new Widget()));
        setEditing(false);

        close();
    };

    const ImageSelector = () => (
        <ResizableImageEditor
            aspectRatio={16 / 9}
            maxWidth={1000}
            editUrl={widget?.content || ''}
            onChange={action((blob) => (file.current = blob))}
        />
    );

    const handleWidgetType = action((e: ChangeEvent<HTMLInputElement>) => {
        update(widget, {
            type: e.target.value
        });
    });

    const handleWidgetContent = action(
        (e: ChangeEvent<HTMLInputElement>) => (widget.content = e.target.value)
    );

    return (
        <div style={styles.container}>
            <hr />
            <label>{t('widgets.type')}</label>
            <Select
                data-testid={qaLabelCampaigns.editModal.dropdownwidget}
                value={widget.type}
                onChange={handleWidgetType}>
                <option value="image">{t('widgets.image')}</option>
                <option value="video">{t('widgets.video')}</option>
                <option value="text">{t('widgets.text')}</option>
                <option value="promotion">{t('widgets.promotion')}</option>
                <option value="tip">{t('widgets.tip')}</option>
                <option value="rating">{t('widgets.rating')}</option>
                <option value="address_correction">{t('widgets.addressCorrection')}</option>
            </Select>

            {widget.type === 'image' && <ImageSelector />}
            {widget.type === 'video' && (
                <Input
                    value={widget.content}
                    onChange={handleWidgetContent}
                    placeholder={t('widgets.videoPlaceholder')}
                />
            )}
            {widget.type === 'text' && (
                <Input
                    data-testid={qaLabelCampaigns.editModal.textWidget.nameInput}
                    value={widget.content}
                    onChange={handleWidgetContent}
                    placeholder={t('widgets.textPlaceholder')}
                />
            )}
            {widget.type === 'promotion' && (
                <div style={styles.promotionContainer}>
                    <div style={styles.imageSelectContainer}>
                        <ImageSelector />
                    </div>
                    <div style={styles.widgetContainer}>
                        {promotionInputs.map((item) => (
                            <InputSection {...{ widget, item }} />
                        ))}
                        <WidgetTriggerSelector widget={widget} />
                    </div>
                </div>
            )}

            {widget.type === 'rating' && (
                <div style={styles.widgetContainer}>
                    {ratingInputs.map((item) => (
                        <InputSection {...{ widget, item }} />
                    ))}
                    <WidgetTriggerSelector widget={widget} />
                </div>
            )}
            {widget.type === 'address_correction' && (
                <div style={styles.widgetContainer}>
                    <WidgetTriggerSelector widget={widget} checked disabled />
                </div>
            )}
            {loading ? (
                <LoadingIndicator />
            ) : (
                <div style={styles.buttonContainer}>
                    <Button onClick={close}>{t('widgets.cancel')}</Button>
                    <Button
                        style={styles.addButton}
                        data-testid={qaLabelCampaigns.editModal.widgedAddBtn}
                        onClick={addWidget}>
                        {!editing ? t('widgets.add') : t('widgets.saveChanges')}
                    </Button>
                </div>
            )}
            <hr />
        </div>
    );
});

export default WidgetEditor;
